'use client';

import { getToolConfig } from '../components/tool-config';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  collectSubagentActivity,
  indexSubagentActivities,
  type SubagentActivityCount,
  type SubagentActivityItem,
  type SubagentActivityNoun,
} from './subagent-activity';
import {
  indexSubagentTasks,
  readSubagentTask,
  type SubagentTaskDetails,
} from './subagent-task';
import {
  latestToolActivity,
  parallelAgentStatus,
  type ParallelAgentStatus,
} from './parallel-agent-batch';
import type { ParallelAgentExecution } from './parallel-agent-execution';

const ACTIVITY_COUNT_KEYS: Record<
  SubagentActivityNoun,
  'activityFile' | 'activitySearch' | 'activityCommand' | 'activityAction'
> = {
  file: 'activityFile',
  search: 'activitySearch',
  command: 'activityCommand',
  action: 'activityAction',
};

export type ParallelAgentActivityLine = {
  label: string;
  target?: string;
  isLive: boolean;
};

export type ParallelAgentRowCopy = {
  name: string;
  title: string | undefined;
  status: ParallelAgentStatus;
  statusLabel: string;
  activity: ParallelAgentActivityLine;
};

export function useIndexedSubagentSources({
  messages,
  parentToolCallIds,
}: {
  messages: TerraUIMessage[];
  parentToolCallIds: readonly string[];
}) {
  const parentKey = parentToolCallIds.join('\0');

  return useMemo(() => {
    const ids = parentKey.length > 0 ? parentKey.split('\0') : [];

    return {
      tasks: indexSubagentTasks({ messages, parentToolCallIds: ids }),
      activities: indexSubagentActivities({ messages, parentToolCallIds: ids }),
    };
  }, [messages, parentKey]);
}

export function useParallelAgentRowCopy({
  execution,
  messages,
  task,
  activity,
}: {
  execution: ParallelAgentExecution;
  messages: TerraUIMessage[];
  task?: SubagentTaskDetails;
  activity?: { items: SubagentActivityItem[]; counts: SubagentActivityCount[] };
}): ParallelAgentRowCopy {
  const t = useTranslations('designSystem.chat.parallelAgents');
  const tStatus = useTranslations(
    'designSystem.chat.parallelAgents.agentStatus',
  );
  const isNeo = execution.kind === 'neo';
  const config = getToolConfig(execution.agentType);
  const status = parallelAgentStatus(execution);
  const resolvedTask =
    task ??
    readSubagentTask({
      messages,
      toolCallId: execution.toolCallId,
    });
  const resolvedActivity =
    activity ??
    collectSubagentActivity({
      messages,
      parentToolCallId: execution.toolCallId,
    });
  const countsSummary = formatCounts({
    counts: resolvedActivity.counts,
    format: (item) => t(ACTIVITY_COUNT_KEYS[item.noun], { count: item.count }),
  });
  const statusLabel = tStatus(status);

  return {
    name: isNeo ? t('neoAgentName') : config.name,
    title: resolvedTask.title ?? execution.title,
    status,
    statusLabel,
    activity: activityLine({
      status,
      latest: latestToolActivity(resolvedActivity.items),
      countsSummary,
      error: resolvedTask.error,
      statusLabel,
      fallbackRunningLabel: isNeo
        ? t('neoAgentRunning')
        : (config.runningText ?? t('agentStarting')),
    }),
  };
}

function formatCounts({
  counts,
  format,
}: {
  counts: SubagentActivityCount[];
  format: (item: SubagentActivityCount) => string;
}): string | undefined {
  return counts.length > 0 ? counts.map(format).join(', ') : undefined;
}

function activityLine({
  status,
  latest,
  countsSummary,
  error,
  statusLabel,
  fallbackRunningLabel,
}: {
  status: ParallelAgentStatus;
  latest: ReturnType<typeof latestToolActivity>;
  countsSummary: string | undefined;
  error: string | undefined;
  statusLabel: string;
  fallbackRunningLabel: string;
}): ParallelAgentActivityLine {
  if (status === 'running') {
    return latest
      ? { label: latest.label, target: latest.target, isLive: true }
      : { label: fallbackRunningLabel, isLive: true };
  }

  if (status === 'error') {
    return { label: error ?? statusLabel, isLive: false };
  }

  if (status === 'completed' && countsSummary) {
    return { label: `${statusLabel} · ${countsSummary}`, isLive: false };
  }

  return { label: statusLabel, isLive: false };
}
